import { ContentUtil } from '@csp/csp-common-content';
import { EnumUtil } from '@csp/csp-common-enum-util';
import {
  BioAnalyteValueType,
  BioComplianceStatusValueType,
  BioSampleDeliveryLabConfig,
  BioSampleDeliveryLabReasonType,
  BioSampleProcessedValueType,
  BioSampleStatusValueType,
  BioSampleValueType,
  CollectionStatusValueType,
  AnySampleStatusValueType,
  ProcessingStatusValueType,
  ShippingStatusValueType,
} from '@csp/csp-common-hbs-model';
import { isDefined } from '@csp/csp-common-util';
import { SelectProps } from '@csp/csp-web-ui';
import uniq from 'lodash/uniq';
import { BioSampleContentService } from '../service/BioSampleContentService';
import { SortedSelectPropsIn } from '../model/SortedSelectPropsIn';
// eslint-disable-next-line @typescript-eslint/ban-types
const prepareEnumValues = (enu: {}): string[] =>
  EnumUtil.asStrings(enu).filter(value => value !== BioSampleStatusValueType.UNKNOWN);

const mapEnumText = (values: string[], content: { enum: Record<string, string> }, defaultValue = ''): string[] =>
  values.map(value => ContentUtil.mapEnum(value, content, defaultValue)).filter(value => value !== '');

// TODO R10 analyte support
const mapAnalyteEnumToText = (analyteTypes: BioAnalyteValueType[], defaultValue = ''): string[] =>
  mapEnumText(analyteTypes, BioSampleContentService.getCommonTexts().analyteType, defaultValue);

// TODO R14 - CMS based sample types was introduced in R14 - enum mapped content should be removed in future release
const mapSampleEnumToText = (sampleTypes: BioSampleValueType[], defaultValue = ''): string[] =>
  mapEnumText(sampleTypes, BioSampleContentService.getCommonTexts().sampleType, defaultValue);

// TODO R14 - CMS based sample types was introduced in R14 - enum mapped content should be removed in future release
const mapProcessedSampleEnumToText = (
  processedSampleTypes: BioSampleProcessedValueType[],
  defaultValue = '',
): string[] =>
  mapEnumText(processedSampleTypes, BioSampleContentService.getCommonTexts().processedSampleType, defaultValue);

const toSortedSelectProps = (
  { supportedValues, currentValues = [], withNoSelectionOption = true }: SortedSelectPropsIn,
  {
    noSelectionText,
    trailingProps = [],
    leadingProps = [],
  }: {
    noSelectionText: string;
    trailingProps?: SelectProps[];
    leadingProps?: SelectProps[];
  },
): SelectProps[] => {
  // 1. Ensure that "extra" values(trailing, leading) in only available once in the list
  // 2. Sort the supported values
  // 3. Return the values is the order - leading, supported, trailing
  const commonValues = [...leadingProps, ...trailingProps].map(prop => prop.value);
  const sortedValues = uniq(
    [...supportedValues, ...currentValues.filter(value => !commonValues.includes(value))].filter(isDefined),
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  return [
    withNoSelectionOption
      ? {
          value: BioSampleStatusValueType.UNKNOWN,
          children: noSelectionText,
          options: {
            secondary: true,
          },
        }
      : undefined,
    ...leadingProps,
    ...sortedValues.map(value => ({
      value,
      children: value,
    })),
    ...trailingProps,
  ].filter(isDefined);
};

const analyteType = (propsIn: SortedSelectPropsIn): SelectProps[] => {
  const hbsTexts = BioSampleContentService.getCommonTexts();
  return toSortedSelectProps(propsIn, {
    noSelectionText: hbsTexts.input.input.analyteTypeNoSelectionOption.label,
    leadingProps: [
      {
        value: hbsTexts.analyteType.enum.NA_COLLECTION_TUBE,
        children: hbsTexts.analyteType.enum.NA_COLLECTION_TUBE,
      },
    ],
    trailingProps: [
      {
        value: hbsTexts.analyteType.enum.OTHER,
        children: hbsTexts.analyteType.enum.OTHER,
      },
    ],
  });
};

const deliveryLab = (
  supportedDeliveryLabs: BioSampleDeliveryLabConfig[],
  currentDeliveryLabs: string[] = [],
  withNoSelectionOption = true,
): SelectProps[] => {
  const hbsTexts = BioSampleContentService.getCommonTexts();
  return toSortedSelectProps(
    {
      supportedValues: supportedDeliveryLabs.map(lab => lab.name),
      currentValues: currentDeliveryLabs,
      withNoSelectionOption,
    },
    {
      trailingProps: [
        {
          value: BioSampleDeliveryLabReasonType.OTHER,
          children: hbsTexts.input.input.deliveryLabOtherOption.label,
        },
      ],
      noSelectionText: hbsTexts.input.input.deliveryLabNoSelectionOption.label,
    },
  );
};

const collectedSampleType = (propsIn: SortedSelectPropsIn): SelectProps[] => {
  const hbsTexts = BioSampleContentService.getCommonTexts();
  return toSortedSelectProps(propsIn, {
    noSelectionText: hbsTexts.input.input.sampleTypeNoSelectionOption.label,
    trailingProps: [
      {
        value: hbsTexts.sampleType.enum.OTHER,
        children: hbsTexts.sampleType.enum.OTHER,
      },
    ],
  });
};

const aliquotSampleType = (propsIn: SortedSelectPropsIn): SelectProps[] => {
  const hbsTexts = BioSampleContentService.getCommonTexts();
  return toSortedSelectProps(propsIn, {
    noSelectionText: hbsTexts.input.input.sampleTypeProcessedNoSelectionOption.label,
    trailingProps: [
      {
        value: hbsTexts.processedSampleType.enum.OTHER,
        children: hbsTexts.processedSampleType.enum.OTHER,
      },
    ],
  });
};

const toStatusSelectProp = (status: AnySampleStatusValueType, cms: string): SelectProps => ({
  value: status,
  children: cms ?? status,
});

const toStatusSelectProps = (showSampleStatuses: AnySampleStatusValueType[] = []): SelectProps[] => {
  const { collectionStatusValueType } = BioSampleContentService.getCollectionTexts();
  const { processingStatusValueType } = BioSampleContentService.getProcessingTexts();
  const { shippingStatusValueType } = BioSampleContentService.getShippingTexts();
  return [
    { status: CollectionStatusValueType.COLLECTED, text: collectionStatusValueType.enum.COLLECTED },
    { status: CollectionStatusValueType.NOT_COLLECTED, text: collectionStatusValueType.enum.NOT_COLLECTED },
    { status: CollectionStatusValueType.ALIQUOTED, text: collectionStatusValueType.enum.ALIQUOTED },
    { status: ProcessingStatusValueType.PROCESSED, text: processingStatusValueType.enum.PROCESSED },
    { status: ShippingStatusValueType.SHIPPED, text: shippingStatusValueType.enum.SHIPPED },
    { status: ShippingStatusValueType.USED, text: shippingStatusValueType.enum.USED },
  ]
    .filter(({ status }) => showSampleStatuses.includes(status))
    .toSorted((a, b) => a.status.localeCompare(b.status))
    .map(({ status, text }) => toStatusSelectProp(status, text));
};

const toComplianceSelectProps = (): SelectProps[] => [
  ...prepareEnumValues(BioComplianceStatusValueType).map(value => ({
    value,
    children: ContentUtil.mapEnum(value, BioSampleContentService.getCommonTexts().bioComplianceStatusValueType),
  })),
];

export const BioSampleSelectPropsMapper = {
  mapAnalyteEnumToText,
  mapSampleEnumToText,
  mapProcessedSampleEnumToText,
  analyteType,
  deliveryLab,
  collectedSampleType,
  aliquotSampleType,
  /** only for unit test **/
  toStatusSelectProp,
  toStatusSelectProps,
  toComplianceSelectProps,
};
